// =============================================================================
// ResetPasswordPage — choose a new password from an emailed link
// =============================================================================
// Reads the single-use token from `?token=...`, validates the new password
// client-side (mirrors the server rule), and posts to the public
// reset-password endpoint. On success the API has invalidated all sessions,
// so the user is directed back to sign in.
// =============================================================================

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '@/lib/api';
import { Button, Input, Card } from '@tkpod/ui';

// Must match ResetPasswordDto on the API.
const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (!PASSWORD_RULE.test(password)) {
      setError(
        'Password must be at least 8 characters and include an uppercase ' +
          'letter, a lowercase letter, a number, and a special character.',
      );
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/v1/auth/reset-password', {
        token,
        newPassword: password,
      });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'This reset link is invalid or has expired.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600">
            <span className="text-lg font-bold text-white">TK</span>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">
            Choose a new password
          </h1>
        </div>

        <Card>
          {done ? (
            <div className="p-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
                <svg
                  className="h-6 w-6 text-success"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m4.5 12.75 6 6 9-13.5"
                  />
                </svg>
              </div>
              <h2 className="mb-2 text-lg font-semibold text-text-primary">
                Password updated
              </h2>
              <p className="mb-6 text-sm text-text-secondary">
                Your password has been changed and all sessions were signed
                out. Please sign in with your new password.
              </p>
              <Link to="/login">
                <Button className="w-full">Sign in</Button>
              </Link>
            </div>
          ) : !token ? (
            <div className="p-6 text-center">
              <h2 className="mb-2 text-lg font-semibold text-text-primary">
                Missing token
              </h2>
              <p className="mb-6 text-sm text-text-secondary">
                This reset link is incomplete. Please request a new one.
              </p>
              <Link to="/forgot-password">
                <Button variant="outline" className="w-full">
                  Request a new link
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 p-6">
              {error && (
                <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
                  {error}
                </div>
              )}

              <Input
                label="New password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter a new password"
                required
                autoComplete="new-password"
                autoFocus
              />

              <Input
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter your new password"
                required
                autoComplete="new-password"
              />

              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !password || !confirm}
              >
                {submitting ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
